// src/pages/SSOCallback.jsx
// Page intermédiaire : reçoit le token SSO WordPress et échange via /auth/sso/wordpress
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, CircularProgress, Typography, Alert, Button, alpha } from "@mui/material";
import API from "../api";

const P = { navy:"#1e3a5f", red:"#c0392b", slate:{ 50:"#f8fafc", 500:"#64748b" } };

export default function SSOCallback() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const params    = new URLSearchParams(location.search);
  const token     = params.get("token")     || "";
  const redirect  = params.get("redirect")  || "/exams";
  const labId     = params.get("lab_id")    || "";
  // Mode d'examen demandé (learn | test), transmis depuis le bouton cliqué
  // dans le dashboard WordPress, relayé par le handler PHP SSO. Sans ce
  // paramètre, ExamPass.jsx retombe sur son comportement par défaut
  // (mode Test complet) même quand "Mode apprentissage" a été cliqué.
  const mode      = params.get("mode")      || "";

  const [status,  setStatus]  = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Token SSO manquant dans l'URL.");
      return;
    }

    (async () => {
      try {
        // Échanger le token WP contre des tokens ExamOnline
        const res = await API.post("/auth/sso/wordpress", { token });
        const data = res.data;

        // Stocker les tokens
        localStorage.setItem("access_token",  data.access_token);
        localStorage.setItem("refresh_token", data.refresh_token);
        localStorage.setItem("token_type",    data.token_type || "bearer");
        localStorage.setItem("is_admin",      String(Boolean(data.is_admin)));

        window.dispatchEvent(new Event("auth-changed"));
        setStatus("success");

        // Rediriger vers l'examen ou la page demandée, en conservant le
        // mode (learn/test) s'il a été précisé.
        const base = labId ? `/exams/${labId}` : redirect;
        const dest = mode ? `${base}${base.includes("?") ? "&" : "?"}mode=${mode}` : base;
        setTimeout(() => navigate(dest, { replace: true }), 800);

      } catch (err) {
        setStatus("error");
        setMessage(
          err?.response?.data?.detail ||
          "Échec de la connexion SSO. Veuillez vous connecter manuellement."
        );
      }
    })();
  }, [token]);

  return (
    <Box sx={{ minHeight:"78vh", display:"flex", alignItems:"center",
      justifyContent:"center", background:P.slate[50] }}>
      <Box sx={{ textAlign:"center", maxWidth:400, p:4 }}>

        {/* Logo */}
        <Box sx={{ width:56, height:56, borderRadius:3, mx:"auto", mb:3,
          background:P.navy, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <Typography sx={{ color:"white", fontSize:"1.5rem" }}>📝</Typography>
        </Box>

        {status === "loading" && (
          <>
            <CircularProgress sx={{ color:P.navy, mb:2 }} />
            <Typography variant="h6" fontWeight={700} color={P.navy}>
              Connexion en cours…
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt:1 }}>
              Validation de votre session WordPress
            </Typography>
          </>
        )}

        {status === "success" && (
          <>
            <Typography sx={{ fontSize:"2.5rem", mb:1 }}>✅</Typography>
            <Typography variant="h6" fontWeight={700} color="#27ae60">
              Connexion réussie !
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt:1 }}>
              Redirection en cours…
            </Typography>
          </>
        )}

        {status === "error" && (
          <>
            <Alert severity="error" sx={{ mb:2, borderRadius:"10px", textAlign:"left" }}>
              {message}
            </Alert>
            <Button variant="contained" onClick={() => navigate("/login")}
              sx={{ background:P.navy, "&:hover":{ background:"#16314f" },
                textTransform:"none", borderRadius:"8px" }}>
              Se connecter manuellement
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}